import {
    ChangeDetectorRef,
    Component,
    computed,
    ElementRef,
    EventEmitter,
    Input,
    NgZone,
    OnInit,
    Output,
    signal,
} from "@angular/core";
import { S25Util } from "../../util/s25-util";
import { TypeManagerDecorator } from "../../main/type.map.service";
import { TaskViewApi } from "../s25-task-view/s25.task.view.component";
import { S25LoadingApi } from "../s25-loading/loading.api";
import { TaskChange, TaskService } from "../../services/task/task.service";
import { Task } from "../../pojo/Task";
import TaskListTaskItem = Task.TaskListTaskItem;
import { TaskNormalizeUtil } from "../../services/task/task.normalize.util";
import { TaskUtil } from "./task.util";
import { TaskCompetingUtil } from "./task.competing.util";

/**
 * S25TaskButtonsComponent is usually used to complete multiple tasks at once, often displayed above and below a table
 */
@TypeManagerDecorator("s25-ng-task-buttons")
@Component({
    selector: "s25-ng-task-buttons",
    template: `
        @if (init) {
            <div>
                <s25-loading-inline [model]="{}"></s25-loading-inline>
                <div>
                    <button
                        class="aw-button aw-button--primary c-margin-right--single c-margin-bottom--half"
                        (click)="approve()"
                        [disabled]="!modelBean || !modelBean.tasks || modelBean.tasks.length === 0"
                    >
                        Approve Checked
                    </button>
                    <button
                        class="aw-button aw-button--primary"
                        (click)="deny(3)"
                        [disabled]="!modelBean || !modelBean.tasks || modelBean.tasks.length === 0"
                    >
                        Deny Checked
                    </button>
                </div>

                <s25-task-competing-confirmation
                    [show]="showWaitlistConfirm()"
                    [message]="competingMessage()"
                    [count]="conflcitCount()"
                    (confirm)="handleCompetingConfirm($event)"
                    (cancel)="cancelCompetingConfirm()"
                ></s25-task-competing-confirmation>
            </div>
        }
    `,
    standalone: false,
})
export class S25TaskButtonsComponent implements OnInit {
    @Input() modelBean: { eventId: number; tasks: TaskListTaskItem[] };
    @Output() onRefresh = new EventEmitter();

    init: boolean = false;

    showWaitlistConfirm = signal<boolean>(false);
    clickApproved = signal<boolean>(false);
    conflcitCount = signal<number>(null);
    pendingCompetingInfo = signal<TaskCompetingUtil.CompetingTasksInfo | null>(null);
    competingMessage = computed<string>(() => {
        const info = this.pendingCompetingInfo();
        if (!info) return "";
        return TaskCompetingUtil.getConfirmationMessage(info.count, "Approving these tasks");
    });
    pendingTasks = signal<TaskChange[]>([]);

    constructor(
        private elementRef: ElementRef,
        private cd: ChangeDetectorRef,
        private zone: NgZone,
    ) {
        this.elementRef.nativeElement.angBridge = this;
    }

    ngOnInit() {
        this.init = true;
    }

    approve() {
        this.onActionClick(Task.States.Assigned);
    }

    deny() {
        this.onActionClick(Task.States.Denied);
    }

    async onActionClick(action: Task.State) {
        this.setLoading(true);
        let tasks: TaskChange[] = this.modelBean.tasks.map(TaskNormalizeUtil.taskListToTaskChange);

        // Check for competing tasks on approval
        if (action === Task.States.Assigned && !this.clickApproved()) {
            const spaceCompetes = this.modelBean.tasks.flatMap((task) =>
                task.competes ? task.competes.flatMap((compete) => compete) : [],
            );

            if (spaceCompetes.length > 0) {
                // Analyze competing tasks
                const allCompetes = spaceCompetes.flatMap((c) => (Array.isArray(c) ? c : [c]));
                const waitListEventIds = Array.from(new Set(allCompetes.map((space) => space.conflictId)));

                // Build competing info manually since we don't have ManageWaitlistTaskInfo here
                const competingInfo: TaskCompetingUtil.CompetingTasksInfo = {
                    hasCompetingTasks: true,
                    count: waitListEventIds.length,
                    tasksToNotify: [], // Will be handled by backend in this context
                    competingEventIds: waitListEventIds,
                };

                this.conflcitCount.set(waitListEventIds.length);
                this.pendingCompetingInfo.set(competingInfo);
                this.pendingTasks.set(tasks);
                this.clickApproved.set(true);
                this.showWaitlistConfirm.set(true);
                this.setLoading(false);
                return;
            }
        }

        await this.executeTaskChange(tasks, action);
    }

    async executeTaskChange(tasks: TaskChange[], action: Task.State) {
        const [resp, err] = await S25Util.Maybe(TaskService.changeTask(tasks, action));
        this.setLoading(false);

        if (err) {
            S25Util.showError(err);
        }

        let el: any = document;
        TaskViewApi.reset(el);
    }

    /**
     * Handle user's response to competing tasks confirmation
     */
    async handleCompetingConfirm(shouldDenyCompeting: boolean) {
        this.showWaitlistConfirm.set(false);
        this.clickApproved.set(false);

        const tasks = this.pendingTasks();

        // Note: In this component, we don't have access to the full task details needed
        // to deny competing tasks, so we just approve. The actual denial would need
        // to be coordinated with the backend or require more task information.
        await this.executeTaskChange(tasks, Task.States.Assigned);

        // Clear pending state
        this.pendingCompetingInfo.set(null);
        this.pendingTasks.set([]);
    }

    /**
     * Cancel the competing tasks confirmation
     */
    cancelCompetingConfirm() {
        this.showWaitlistConfirm.set(false);
        this.clickApproved.set(false);
        this.pendingCompetingInfo.set(null);
        this.pendingTasks.set([]);
        this.setLoading(false);
    }

    setLoading(yes: boolean) {
        if (yes) {
            S25LoadingApi.init(this.elementRef.nativeElement);
        } else {
            S25LoadingApi.destroy(this.elementRef.nativeElement);
        }
        this.cd.detectChanges();
    }
}
